// 📄 pages/SAP-BTP/components/DiagramaIflow.tsx
// Visor del diagrama de un iFlow (docs/plans/plan-visor-diagrama-iflows.md,
// E1 + E2). Dibuja en SVG el layout que trae el propio .iflw (mismas coordenadas
// que el editor de Integration Suite): pool, participantes, pasos, eventos,
// gateways y flechas. Zoom con la rueda o los botones, pan arrastrando y clic
// en un elemento para ver su detalle (adaptador, paso o ruta). Minimapa al
// hacer zoom y exportación a PNG/SVG (o adjuntar el PNG a la documentación).
// El dueño puede editar cada valor ahí mismo, en el panel (E3): los cambios se
// acumulan como "pendientes" y se descargan o guardan juntos, en un solo .zip.
//
// Los valores de los adaptadores (hosts, alias, Cloud Connector) llegan
// reales solo si el backend decidió que el usuario es el dueño
// (`valoresVisibles`); aun así se muestran ocultos hasta pedirlos. Lo mismo
// con los valores constantes de los Content Modifier. La exportación dibuja
// solo el diagrama: nunca incluye valores.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeftRight, Box, Check, ChevronDown, Clock, Code, Download, Eye, EyeOff, FileCode, Filter, ImageDown, Mail, Maximize2, Merge, Minimize2,
  BookmarkPlus, KeyRound, Paperclip, Pencil, PenLine, RefreshCw, RotateCcw, Save, Scan, Shuffle, Split, Wand2, Workflow, X, Zap, ZoomIn, ZoomOut
} from 'lucide-react';
import {
  AristaDiagrama, DiagramaIflow as Diagrama, IflowApiError, NodoDiagrama, descargarAdaptado, guardarAdaptadoComoVersion, obtenerDiagrama
} from '../../../services/iflowService.ts';
import { etiquetaConectividad } from './DetalleTecnico.tsx';
import SelectorPerfil, { tipoParaCampo } from './SelectorPerfil.tsx';
import AplicarPerfilModal from './AplicarPerfilModal.tsx';
import GuardarEnPerfilModal from './GuardarEnPerfilModal.tsx';

interface DiagramaIflowProps {
  orgId: string;
  iflowId: string;
  versionActual: number;
  // Base del nombre de los archivos exportados (el ID técnico del iFlow).
  nombreArchivo: string;
  // Si está, aparece "Adjuntar a la documentación" (quien puede gestionar el iFlow y tiene cupo).
  onAdjuntar?: (archivo: File) => Promise<void>;
  // Solo el dueño: "Editar" en el panel de un adaptador o Content Modifier abre
  // "Adaptar valores" enfocado en ese elemento (prefijo de clave, ej. "adaptador:MessageFlow_2:").
  onEditar?: (foco: string) => void;
  // Edición en línea (solo el dueño, junto con onEditar): guardar como versión
  // nueva (no en una org archivada) y qué hacer después de guardar.
  puedeGuardar?: boolean;
  onGuardado?: (version: number, cambiados: number) => Promise<void> | void;
  // Diagrama ya armado (ej. la vista previa de un boceto del diseñador, E5):
  // no se pide a la API y no hay selector de revisión.
  diagramaFijo?: Diagrama;
}

type Caja = { x: number; y: number; w: number; h: number };
type Seleccion = { tipo: 'nodo'; nodo: NodoDiagrama } | { tipo: 'arista'; arista: AristaDiagrama } | null;

const MARGEN = 40;
const ZOOM_MIN = 0.15;
const ZOOM_MAX = 4;

// Nombres de SAP para las variantes más comunes (el resto se muestra tal cual).
const NOMBRES_VARIANTE: Record<string, string> = {
  GroovyScript: 'Groovy Script',
  JavaScript: 'JavaScript',
  Enricher: 'Content Modifier',
  MessageMapping: 'Message Mapping',
  XSLTMapping: 'XSLT Mapping',
  OperationMapping: 'Operation Mapping',
  ExternalCall: 'Request Reply',
  GeneralSplitter: 'General Splitter',
  IterativeSplitter: 'Iterating Splitter',
  ExclusiveGateway: 'Router',
  SequentialMulticast: 'Sequential Multicast',
  ParallelMulticast: 'Parallel Multicast',
  Gather: 'Gather',
  Join: 'Join',
  ContentEnricher: 'Content Enricher',
  ProcessCallElement: 'Process Call',
  MessageStartEvent: 'Inicio (mensaje)',
  MessageEndEvent: 'Fin (mensaje)'
};

const NOMBRES_TIPO: Record<string, string> = {
  pool: 'Integration Process',
  participante: 'Participante',
  paso: 'Paso',
  inicio: 'Evento de inicio',
  fin: 'Evento de fin',
  evento: 'Evento',
  gateway: 'Gateway',
  subproceso: 'Subproceso',
  otro: 'Elemento'
};

const NOMBRES_EVENTO: Record<string, string> = { message: 'mensaje', timer: 'timer', error: 'error', terminate: 'terminar', escalation: 'escalamiento', none: 'simple' };

// Campos sensibles (functions/services/iflowZip.js, CAMPOS_SENSIBLES).
export const NOMBRES_CAMPO: Record<string, string> = {
  address: 'Dirección',
  httpAddressWithoutQuery: 'Dirección',
  httpAddressQuery: 'Query',
  credentialName: 'Alias de credencial',
  location_id: 'Location ID (Cloud Connector)',
  privateKeyAlias: 'Alias de clave privada',
  proxyHost: 'Proxy host',
  host: 'Host',
  url: 'URL'
};

const tipoLegible = (n: NodoDiagrama) => {
  if (n.tipo === 'participante') return n.subtipo === 'emisor' ? 'Emisor' : 'Receptor';
  if (['inicio', 'fin', 'evento'].includes(n.tipo)) return `${NOMBRES_TIPO[n.tipo]} (${NOMBRES_EVENTO[n.subtipo] || n.subtipo})`;
  return NOMBRES_VARIANTE[n.variante] || NOMBRES_VARIANTE[n.subtipo] || n.variante || n.subtipo || NOMBRES_TIPO[n.tipo];
};

const IconoPaso = ({ n, ...props }: { n: NodoDiagrama } & React.SVGProps<SVGSVGElement>) => {
  const Icono = n.subtipo === 'Script' ? Code
    : n.subtipo === 'Mapping' ? Shuffle
    : n.subtipo === 'Enricher' ? PenLine
    : n.subtipo === 'ExternalCall' ? ArrowLeftRight
    : n.subtipo === 'Splitter' ? Split
    : /Enricher|Gather|Join|Aggregator/.test(n.subtipo + n.variante) ? Merge
    : /ProcessCall|LoopingProcess/.test(n.subtipo + n.variante) ? Workflow
    : /Filter/.test(n.subtipo) ? Filter
    : Box;
  return <Icono {...(props as any)} />;
};

/** Parte un nombre en hasta `max` líneas de ~`ancho` px (aprox. 5.6 px por carácter a 10px). */
const lineas = (texto: string, ancho: number, max = 3): string[] => {
  const porLinea = Math.max(4, Math.floor(ancho / 5.6));
  const salida: string[] = [];
  let actual = '';
  for (const palabra of texto.split(/\s+/).filter(Boolean)) {
    const candidata = actual ? `${actual} ${palabra}` : palabra;
    if (candidata.length <= porLinea) {
      actual = candidata;
      continue;
    }
    if (actual) salida.push(actual);
    actual = palabra.length > porLinea ? `${palabra.slice(0, porLinea - 1)}…` : palabra;
  }
  if (actual) salida.push(actual);
  if (salida.length > max) return [...salida.slice(0, max - 1), `${salida[max - 1].slice(0, porLinea - 1)}…`];
  return salida;
};

/** Punto medio del tramo más largo de una flecha (donde va su etiqueta). */
const medioDeFlecha = (puntos: [number, number][]): [number, number] => {
  let mejor = 0;
  let largo = -1;
  for (let i = 0; i < puntos.length - 1; i++) {
    const l = Math.hypot(puntos[i + 1][0] - puntos[i][0], puntos[i + 1][1] - puntos[i][1]);
    if (l > largo) { largo = l; mejor = i; }
  }
  return [(puntos[mejor][0] + puntos[mejor + 1][0]) / 2, (puntos[mejor][1] + puntos[mejor + 1][1]) / 2];
};

// ── Exportación ──
// El SVG de la pantalla usa clases de Tailwind (y variables del tema): para
// que el archivo se vea igual fuera de la app, se copian los estilos
// calculados de cada elemento como atributo `style`.
const ESTILOS_EXPORTADOS = ['fill', 'fill-opacity', 'stroke', 'stroke-width', 'stroke-dasharray', 'stroke-opacity', 'stroke-linecap', 'stroke-linejoin',
  'opacity', 'color', 'font-family', 'font-size', 'font-weight', 'paint-order', 'text-anchor'];
const NS_SVG = 'http://www.w3.org/2000/svg';

const svgAutonomo = (svg: SVGSVGElement, caja: Caja, fondo: string): string => {
  const copia = svg.cloneNode(true) as SVGSVGElement;
  const originales = [svg, ...Array.from(svg.querySelectorAll('*'))];
  const copias = [copia, ...Array.from(copia.querySelectorAll('*'))];
  originales.forEach((el, i) => {
    const calculado = getComputedStyle(el);
    copias[i].setAttribute('style', ESTILOS_EXPORTADOS.map((p) => `${p}:${calculado.getPropertyValue(p)}`).join(';'));
    copias[i].removeAttribute('class');
  });
  copia.querySelectorAll('title').forEach((t) => t.remove());
  copia.setAttribute('xmlns', NS_SVG);
  copia.setAttribute('viewBox', `${caja.x} ${caja.y} ${caja.w} ${caja.h}`);
  copia.setAttribute('width', String(Math.round(caja.w)));
  copia.setAttribute('height', String(Math.round(caja.h)));
  const base = document.createElementNS(NS_SVG, 'rect');
  [['x', caja.x], ['y', caja.y], ['width', caja.w], ['height', caja.h], ['fill', fondo]].forEach(([k, v]) => base.setAttribute(String(k), String(v)));
  copia.insertBefore(base, copia.firstChild);
  return new XMLSerializer().serializeToString(copia);
};

/** Rasteriza el SVG a PNG (x2 para que se lea bien, con un tope de 8000 px por lado). */
const svgAPng = (svgTexto: string, ancho: number, alto: number) => new Promise<Blob>((resolver, rechazar) => {
  const url = URL.createObjectURL(new Blob([svgTexto], { type: 'image/svg+xml;charset=utf-8' }));
  const img = new Image();
  img.onload = () => {
    const escala = Math.min(2, 8000 / Math.max(ancho, alto));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(ancho * escala);
    canvas.height = Math.round(alto * escala);
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    canvas.toBlob((b) => (b ? resolver(b) : rechazar(new Error('No se pudo generar el PNG.'))), 'image/png');
  };
  img.onerror = () => {
    URL.revokeObjectURL(url);
    rechazar(new Error('No se pudo generar la imagen.'));
  };
  img.src = url;
});

const descargarBlob = (blob: Blob, nombre: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Espera a que React aplique el cambio (exportar sin la selección resaltada).
// setTimeout y no requestAnimationFrame: rAF se pausa con la pestaña en segundo plano.
const esperarRender = () => new Promise<void>((r) => setTimeout(r, 50));

// Halo del color del fondo detrás de las etiquetas: se leen aunque crucen líneas.
const HALO = { stroke: 'rgb(var(--c-bg))', strokeWidth: 3, paintOrder: 'stroke' } as const;

const LEYENDA_VALORES = 'Solo el autor del iFlow y el owner de la organización ven los valores reales.';

// Contenedores primero (quedan detrás), luego el resto.
const ORDEN: Record<string, number> = { pool: 0, subproceso: 1, participante: 2 };

export default function DiagramaIflow({
  orgId, iflowId, versionActual, nombreArchivo, onAdjuntar, onEditar, puedeGuardar = false, onGuardado, diagramaFijo
}: DiagramaIflowProps) {
  const [version, setVersion] = useState(versionActual);
  const [cache, setCache] = useState<Record<number, Diagrama>>({});
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seleccion, setSeleccion] = useState<Seleccion>(null);
  const [mostrarValores, setMostrarValores] = useState(false);
  const [ampliado, setAmpliado] = useState(false);
  const [vista, setVista] = useState<Caja | null>(null);
  const [menuExportar, setMenuExportar] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [avisoExportar, setAvisoExportar] = useState<{ ok: boolean; texto: string } | null>(null);
  // Edición en línea: valores cambiados (clave -> valor nuevo), cuál se está editando y su texto.
  const [pendientes, setPendientes] = useState<Record<string, string>>({});
  const [editando, setEditando] = useState<string | null>(null);
  const [textoEdicion, setTextoEdicion] = useState('');
  const [ocupadoPendientes, setOcupadoPendientes] = useState<null | 'descargar' | 'guardar'>(null);
  const [errorPendientes, setErrorPendientes] = useState<string | null>(null);
  // "Desde perfil" para el valor que se está editando (tipo sugerido según el campo).
  const [desdePerfil, setDesdePerfil] = useState<{ campo: string; clave: string; original: string } | null>(null);
  // P3 de perfiles: aplicar uno completo / guardar los pendientes en uno.
  const [aplicandoPerfil, setAplicandoPerfil] = useState(false);
  const [guardandoEnPerfil, setGuardandoEnPerfil] = useState(false);
  const [avisoPerfil, setAvisoPerfil] = useState<string | null>(null);

  const svgRef = useRef<SVGSVGElement>(null);
  const lienzoRef = useRef<HTMLDivElement>(null);
  const arrastre = useRef<{ x: number; y: number; vista: Caja; movido: boolean } | null>(null);
  // El click llega DESPUÉS del pointerup: recuerda si ese gesto fue un arrastre.
  const fueArrastre = useRef(false);

  const diagrama = diagramaFijo ?? cache[version];

  // Una revisión nueva (subida desde este mismo modal) pasa a ser la vista por defecto.
  useEffect(() => setVersion(versionActual), [versionActual]);

  useEffect(() => {
    if (diagramaFijo || cache[version]) return;
    let vigente = true;
    setCargando(true);
    setError(null);
    obtenerDiagrama(orgId, iflowId, version)
      .then((d) => { if (vigente) setCache((c) => ({ ...c, [version]: d })); })
      .catch((err) => {
        if (!vigente) return;
        setError(err instanceof IflowApiError && err.code === 'NO_DIAGRAM'
          ? 'Este iFlow no trae diagrama en el .zip.'
          : err?.message || 'No se pudo cargar el diagrama.');
      })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [orgId, iflowId, version, cache, diagramaFijo]);

  const encuadre = useMemo<Caja | null>(() => {
    if (!diagrama) return null;
    const { x, y, ancho, alto } = diagrama.limites;
    return { x: x - MARGEN, y: y - MARGEN, w: ancho + MARGEN * 2, h: alto + MARGEN * 2 };
  }, [diagrama]);

  // Otro diagrama (revisión): limpiar la selección y volver a ocultar los valores.
  useEffect(() => {
    setSeleccion(null);
    setMostrarValores(false);
    setPendientes({});
    setEditando(null);
    setErrorPendientes(null);
  }, [diagrama]);

  // Encuadrar todo al cargar, al ampliar y al reducir.
  useEffect(() => { setVista(encuadre); }, [ampliado, encuadre]);

  useEffect(() => {
    if (!ampliado) return;
    const alEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setAmpliado(false); };
    window.addEventListener('keydown', alEscape);
    return () => window.removeEventListener('keydown', alEscape);
  }, [ampliado]);

  /** Coordenadas del diagrama para un punto de la pantalla. */
  const aDiagrama = useCallback((clientX: number, clientY: number, v: Caja) => {
    const r = svgRef.current!.getBoundingClientRect();
    // preserveAspectRatio="xMidYMid meet": escala uniforme y centrado.
    const escala = Math.max(v.w / r.width, v.h / r.height);
    const offX = (r.width * escala - v.w) / 2;
    const offY = (r.height * escala - v.h) / 2;
    return { x: v.x - offX + (clientX - r.left) * escala, y: v.y - offY + (clientY - r.top) * escala, escala };
  }, []);

  const zoom = useCallback((factor: number, centro?: { clientX: number; clientY: number }) => {
    setVista((v) => {
      if (!v || !encuadre || !svgRef.current) return v;
      const actual = encuadre.w / v.w;
      const nuevo = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, actual * factor));
      const f = actual / nuevo;
      const r = svgRef.current.getBoundingClientRect();
      const p = centro ? aDiagrama(centro.clientX, centro.clientY, v) : aDiagrama(r.left + r.width / 2, r.top + r.height / 2, v);
      return { x: p.x - (p.x - v.x) * f, y: p.y - (p.y - v.y) * f, w: v.w * f, h: v.h * f };
    });
  }, [encuadre, aDiagrama]);

  const hayVista = vista !== null;
  // La rueda necesita un listener no pasivo para que la página no haga scroll.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const alRodar = (e: WheelEvent) => {
      e.preventDefault();
      zoom(e.deltaY < 0 ? 1.15 : 1 / 1.15, e);
    };
    svg.addEventListener('wheel', alRodar, { passive: false });
    return () => svg.removeEventListener('wheel', alRodar);
    // El <svg> se vuelve a montar al cargar y al ampliar/reducir.
  }, [zoom, diagrama, ampliado, cargando, hayVista]);

  const alPresionar = (e: React.PointerEvent) => {
    if (!vista || e.button !== 0) return;
    fueArrastre.current = false;
    arrastre.current = { x: e.clientX, y: e.clientY, vista, movido: false };
  };
  const alMover = (e: React.PointerEvent) => {
    const a = arrastre.current;
    if (!a || !svgRef.current) return;
    const dx = e.clientX - a.x;
    const dy = e.clientY - a.y;
    if (!a.movido && Math.hypot(dx, dy) < 4) return;
    if (!a.movido) svgRef.current.setPointerCapture(e.pointerId);
    a.movido = true;
    fueArrastre.current = true;
    const { escala } = aDiagrama(e.clientX, e.clientY, a.vista);
    setVista({ ...a.vista, x: a.vista.x - dx * escala, y: a.vista.y - dy * escala });
  };
  const alSoltar = (e: React.PointerEvent) => {
    const a = arrastre.current;
    arrastre.current = null;
    if (a?.movido && svgRef.current?.hasPointerCapture(e.pointerId)) svgRef.current.releasePointerCapture(e.pointerId);
  };
  // Un clic (sin arrastre) selecciona; en el fondo, deselecciona.
  const seleccionar = (s: Seleccion) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fueArrastre.current) return;
    setSeleccion(s);
  };

  const nodos = useMemo(() => [...(diagrama?.nodos || [])].sort((a, b) => (ORDEN[a.tipo] ?? 9) - (ORDEN[b.tipo] ?? 9)), [diagrama]);
  const idSel = seleccion?.tipo === 'nodo' ? seleccion.nodo.id : seleccion?.tipo === 'arista' ? seleccion.arista.id : '';

  const renderNodo = (n: NodoDiagrama) => {
    const sel = n.id === idSel;
    const contorno = sel ? 'stroke-cyan-300' : '';
    const grosor = sel ? 2.5 : 1;
    const al = seleccionar({ tipo: 'nodo', nodo: n });
    const titulo = <title>{`${n.nombre || NOMBRES_TIPO[n.tipo]} · ${tipoLegible(n)}`}</title>;

    if (n.tipo === 'pool' || n.tipo === 'subproceso') {
      return (
        <g key={n.id} onClick={al} className="cursor-pointer">
          {titulo}
          <rect x={n.x} y={n.y} width={n.ancho} height={n.alto} rx={n.tipo === 'pool' ? 2 : 10}
            className={`fill-white/[0.02] ${contorno || 'stroke-gray-500'}`} strokeWidth={grosor} strokeDasharray={n.tipo === 'subproceso' ? '6 4' : undefined} />
          <text x={n.x + 8} y={n.y + 14} className="fill-gray-400" fontSize={11}>{n.nombre || NOMBRES_TIPO[n.tipo]}</text>
        </g>
      );
    }
    if (n.tipo === 'participante') {
      return (
        <g key={n.id} onClick={al} className="cursor-pointer">
          {titulo}
          <rect x={n.x} y={n.y} width={n.ancho} height={n.alto} rx={3} className={`fill-gray-800 ${contorno || 'stroke-gray-400'}`} strokeWidth={grosor} />
          <line x1={n.x} y1={n.y + 22} x2={n.x + n.ancho} y2={n.y + 22} className="stroke-gray-500" strokeWidth={1} />
          <text x={n.x + n.ancho / 2} y={n.y + 15} textAnchor="middle" className="fill-gray-200" fontSize={10}>{lineas(n.nombre, n.ancho - 6, 1)[0]}</text>
        </g>
      );
    }
    if (n.tipo === 'inicio' || n.tipo === 'fin' || n.tipo === 'evento') {
      const r = Math.min(n.ancho, n.alto) / 2;
      const cx = n.x + n.ancho / 2;
      const cy = n.y + n.alto / 2;
      const Icono = n.subtipo === 'timer' ? Clock : n.subtipo === 'error' ? Zap : n.subtipo === 'message' ? Mail : null;
      const s = r * 1.1;
      return (
        <g key={n.id} onClick={al} className="cursor-pointer">
          {titulo}
          <circle cx={cx} cy={cy} r={r} className={`fill-gray-800 ${contorno || (n.tipo === 'fin' ? 'stroke-gray-200' : 'stroke-emerald-400')}`}
            strokeWidth={sel ? 3 : n.tipo === 'fin' ? 3 : 1.5} />
          {Icono && <Icono x={cx - s / 2} y={cy - s / 2} width={s} height={s} className="text-gray-300" strokeWidth={2} />}
          {n.nombre && <text x={cx} y={n.y + n.alto + 12} textAnchor="middle" className="fill-gray-400" fontSize={9} {...HALO}>{lineas(n.nombre, 90, 1)[0]}</text>}
        </g>
      );
    }
    if (n.tipo === 'gateway') {
      const cx = n.x + n.ancho / 2;
      const cy = n.y + n.alto / 2;
      const d = 7;
      const paralelo = /Multicast|Parallel|Join|Gather/i.test(n.subtipo);
      return (
        <g key={n.id} onClick={al} className="cursor-pointer">
          {titulo}
          <polygon points={`${cx},${n.y} ${n.x + n.ancho},${cy} ${cx},${n.y + n.alto} ${n.x},${cy}`}
            className={`fill-gray-800 ${contorno || 'stroke-amber-400'}`} strokeWidth={sel ? 2.5 : 1.5} />
          {paralelo
            ? <path d={`M${cx - d},${cy} H${cx + d} M${cx},${cy - d} V${cy + d}`} className="stroke-amber-300" strokeWidth={2} />
            : <path d={`M${cx - d * 0.7},${cy - d * 0.7} L${cx + d * 0.7},${cy + d * 0.7} M${cx + d * 0.7},${cy - d * 0.7} L${cx - d * 0.7},${cy + d * 0.7}`} className="stroke-amber-300" strokeWidth={2} />}
          {/* Nombre arriba del rombo: abajo se encimaba con las etiquetas de las rutas que bajan. */}
          {n.nombre && <text x={cx} y={n.y - 5} textAnchor="middle" className="fill-gray-400" fontSize={9} {...HALO}>{lineas(n.nombre, 100, 1)[0]}</text>}
        </g>
      );
    }
    // Paso (o elemento desconocido): caja con ícono y nombre.
    const texto = lineas(n.nombre || tipoLegible(n), n.ancho - 10);
    const y0 = n.y + n.alto / 2 + 5 - ((texto.length - 1) * 11) / 2;
    return (
      <g key={n.id} onClick={al} className="cursor-pointer">
        {titulo}
        {/* Fondo sólido: el relleno de color es translúcido y dejaba ver las líneas por detrás del texto. */}
        <rect x={n.x} y={n.y} width={n.ancho} height={n.alto} rx={4} className="fill-gray-900" />
        <rect x={n.x} y={n.y} width={n.ancho} height={n.alto} rx={4}
          className={`fill-cyan-500/10 ${contorno || (n.tipo === 'otro' ? 'stroke-gray-500' : 'stroke-cyan-500/60')}`} strokeWidth={grosor} />
        <IconoPaso n={n} x={n.x + 4} y={n.y + 4} width={11} height={11} className="text-cyan-300" strokeWidth={2} />
        <text textAnchor="middle" className="fill-white" fontSize={10}>
          {texto.map((l, i) => <tspan key={i} x={n.x + n.ancho / 2} y={y0 + i * 11}>{l}</tspan>)}
        </text>
      </g>
    );
  };

  const renderArista = (a: AristaDiagrama) => {
    const sel = a.id === idSel;
    const mensaje = a.tipo === 'mensaje';
    const puntos = a.puntos.map((p) => p.join(',')).join(' ');
    const [mx, my] = medioDeFlecha(a.puntos);
    const etiqueta = a.nombre;
    return (
      <g key={a.id} onClick={seleccionar({ tipo: 'arista', arista: a })} className="cursor-pointer">
        <title>{mensaje && a.adaptador ? `${a.adaptador.tipo} · ${a.adaptador.direccion}` : etiqueta || 'Flujo'}</title>
        {/* Trazo ancho e invisible: hace fácil el clic sobre la línea. */}
        <polyline points={puntos} fill="none" stroke="transparent" strokeWidth={10} />
        <polyline points={puntos} fill="none" markerEnd={`url(#${mensaje ? 'flecha-mensaje' : 'flecha'})`}
          className={sel ? 'stroke-cyan-300' : mensaje ? 'stroke-blue-400' : 'stroke-gray-400'}
          strokeWidth={sel ? 2.5 : 1.2} strokeDasharray={mensaje ? '5 4' : undefined} />
        {etiqueta && (
          <text x={mx} y={my - 4} textAnchor="middle" fontSize={9} className={mensaje ? 'fill-blue-300' : 'fill-gray-300'} {...HALO}>
            {lineas(etiqueta, 120, 1)[0]}{a.porDefecto ? ' (por defecto)' : ''}
          </text>
        )}
      </g>
    );
  };

  // ── Minimapa: solo con zoom (si ya se ve todo, no aporta) ──
  const conZoom = !!(vista && encuadre && (vista.w < encuadre.w * 0.98 || vista.x > encuadre.x + 1 || vista.y > encuadre.y + 1
    || vista.x + vista.w < encuadre.x + encuadre.w - 1 || vista.y + vista.h < encuadre.y + encuadre.h - 1));
  const centrarDesdeMinimapa = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!encuadre || (e.type === 'pointermove' && e.buttons !== 1)) return;
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    const x = encuadre.x + ((e.clientX - r.left) / r.width) * encuadre.w;
    const y = encuadre.y + ((e.clientY - r.top) / r.height) * encuadre.h;
    setVista((v) => (v ? { ...v, x: x - v.w / 2, y: y - v.h / 2 } : v));
  };
  const minimapa = diagrama && vista && encuadre && conZoom && (
    <svg
      viewBox={`${encuadre.x} ${encuadre.y} ${encuadre.w} ${encuadre.h}`}
      className="absolute bottom-2 right-2 w-44 max-w-[40%] bg-gray-800/95 border border-white/10 rounded-md cursor-pointer touch-none"
      style={{ aspectRatio: `${encuadre.w} / ${encuadre.h}` }}
      onPointerDown={centrarDesdeMinimapa}
      onPointerMove={centrarDesdeMinimapa}
      aria-label="Minimapa"
    >
      {nodos.map((n) => (
        <rect key={n.id} x={n.x} y={n.y} width={n.ancho} height={n.alto} vectorEffect="non-scaling-stroke"
          className={n.tipo === 'pool' || n.tipo === 'subproceso' ? 'fill-none stroke-gray-500' : n.tipo === 'paso' ? 'fill-cyan-500/50' : 'fill-gray-500'} strokeWidth={1} />
      ))}
      <rect x={vista.x} y={vista.y} width={vista.w} height={vista.h} vectorEffect="non-scaling-stroke" className="fill-cyan-400/10 stroke-cyan-300" strokeWidth={1.5} />
    </svg>
  );

  const selectorPerfil = desdePerfil && (
    <SelectorPerfil
      orgId={orgId}
      tipoSugerido={tipoParaCampo(desdePerfil.campo)}
      // Elegir un valor del perfil ya lo aplica (queda como cambio pendiente, con deshacer).
      onElegir={(valor) => {
        const { clave, original } = desdePerfil;
        setPendientes((p) => {
          const { [clave]: _, ...resto } = p;
          return valor === original ? resto : { ...resto, [clave]: valor };
        });
        setEditando(null);
        setDesdePerfil(null);
      }}
      onCerrar={() => setDesdePerfil(null)}
    />
  );

  // Todos los valores editables del diagrama (dueño): adaptadores y constantes del Content Modifier.
  const editablesDiagrama = useMemo(() => {
    if (!diagrama?.valoresVisibles) return [];
    const nombreNodo = new Map(diagrama.nodos.map((n) => [n.id, n.nombre]));
    return [
      ...diagrama.aristas.flatMap((a) => (a.adaptador?.valores || []).filter((v) => v.clave).map((v) => ({
        clave: v.clave!, original: v.valor, campo: v.campo,
        etiqueta: `${nombreNodo.get(a.hasta) || nombreNodo.get(a.desde) || a.nombre} · ${a.adaptador!.tipo} · ${NOMBRES_CAMPO[v.campo] || v.campo}`
      }))),
      ...diagrama.nodos.flatMap((n) => (n.detalle?.filas || []).filter((f) => f.clave).map((f) => ({
        clave: f.clave!, original: f.valor, campo: '',
        etiqueta: `${n.nombre || 'Content Modifier'} · ${f.grupo}${f.nombre ? `: ${f.nombre}` : ''}`
      })))
    ];
  }, [diagrama]);

  const modalesPerfil = (
    <>
      {aplicandoPerfil && (
        <AplicarPerfilModal
          orgId={orgId}
          valores={editablesDiagrama.map((e) => ({ clave: e.clave, valor: pendientes[e.clave] ?? e.original, etiqueta: e.etiqueta }))}
          onAplicar={(c) => {
            setPendientes((p) => {
              const nuevo = { ...p };
              for (const e of editablesDiagrama) {
                if (!(e.clave in c)) continue;
                if (c[e.clave] === e.original) delete nuevo[e.clave];
                else nuevo[e.clave] = c[e.clave];
              }
              return nuevo;
            });
            setAplicandoPerfil(false);
            setAvisoPerfil(`Se aplicaron ${Object.keys(c).length} cambio${Object.keys(c).length === 1 ? '' : 's'} del perfil: quedan como pendientes.`);
          }}
          onCerrar={() => setAplicandoPerfil(false)}
        />
      )}
      {guardandoEnPerfil && (
        <GuardarEnPerfilModal
          orgId={orgId}
          cambios={editablesDiagrama.filter((e) => e.clave in pendientes).map((e) => ({
            etiqueta: e.etiqueta,
            tipo: tipoParaCampo(e.campo) || (pendientes[e.clave].includes('://') ? 'url' : 'otro'),
            valor: pendientes[e.clave],
            reemplaza: e.original
          }))}
          onListo={(m) => { setGuardandoEnPerfil(false); setAvisoPerfil(m); }}
          onCerrar={() => setGuardandoEnPerfil(false)}
        />
      )}
    </>
  );

  const btnIcono = 'p-1.5 rounded-md border border-white/10 bg-gray-800/90 text-gray-300 hover:text-white hover:border-cyan-500/40 disabled:opacity-40';
  const revisiones = Array.from({ length: versionActual }, (_, i) => versionActual - i);

  const lienzo = (
    <div ref={lienzoRef} className={`relative bg-gray-900 border border-white/10 rounded-xl overflow-hidden ${ampliado ? 'flex-1 min-h-0' : 'h-[60vh] min-h-[320px]'}`}>
      {cargando && <div className="absolute inset-0 flex items-center justify-center text-gray-400"><RefreshCw className="w-5 h-5 animate-spin" /></div>}
      {error && !cargando && <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-gray-400">{error}</div>}
      {diagrama && vista && !cargando && (
        <svg
          ref={svgRef}
          viewBox={`${vista.x} ${vista.y} ${vista.w} ${vista.h}`}
          preserveAspectRatio="xMidYMid meet"
          className="w-full h-full select-none touch-none cursor-grab active:cursor-grabbing"
          onPointerDown={alPresionar}
          onPointerMove={alMover}
          onPointerUp={alSoltar}
          onPointerCancel={alSoltar}
          onClick={() => { if (!fueArrastre.current) setSeleccion(null); }}
          role="img"
          aria-label="Diagrama del iFlow"
        >
          <defs>
            <marker id="flecha" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10 z" className="fill-gray-400" />
            </marker>
            <marker id="flecha-mensaje" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10" fill="none" className="stroke-blue-400" strokeWidth={1.5} />
            </marker>
          </defs>
          {nodos.filter((n) => (ORDEN[n.tipo] ?? 9) < 9).map(renderNodo)}
          {diagrama.aristas.map(renderArista)}
          {nodos.filter((n) => (ORDEN[n.tipo] ?? 9) === 9).map(renderNodo)}
        </svg>
      )}
      {diagrama && !cargando && (
        <div className="absolute top-2 right-2 flex gap-1">
          <button onClick={() => zoom(1.25)} className={btnIcono} aria-label="Acercar" title="Acercar"><ZoomIn className="w-4 h-4" /></button>
          <button onClick={() => zoom(1 / 1.25)} className={btnIcono} aria-label="Alejar" title="Alejar"><ZoomOut className="w-4 h-4" /></button>
          <button onClick={() => setVista(encuadre)} className={btnIcono} aria-label="Ajustar a la pantalla" title="Ajustar a la pantalla"><Scan className="w-4 h-4" /></button>
          <button onClick={() => setAmpliado((a) => !a)} className={btnIcono} aria-label={ampliado ? 'Reducir' : 'Pantalla completa'} title={ampliado ? 'Reducir (Esc)' : 'Pantalla completa'}>
            {ampliado ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      )}
      {!cargando && minimapa}
      {diagrama && !cargando && (
        <p className="absolute bottom-2 left-3 text-[11px] text-gray-500 pointer-events-none">Rueda: zoom · arrastrar: mover · clic: detalle</p>
      )}
    </div>
  );

  const ocultos = !!diagrama?.valoresVisibles && !mostrarValores;
  const botonValores = diagrama?.valoresVisibles && (
    <button onClick={() => setMostrarValores((m) => !m)} className="inline-flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-200">
      {mostrarValores ? <><EyeOff className="w-3.5 h-3.5" /> Ocultar</> : <><Eye className="w-3.5 h-3.5" /> Mostrar valores</>}
    </button>
  );

  // ── Exportar ──
  const exportar = async (formato: 'png' | 'svg' | 'adjuntar') => {
    setMenuExportar(false);
    setAvisoExportar(null);
    if (!svgRef.current || !encuadre || !lienzoRef.current) return;
    setExportando(true);
    try {
      // Sin la selección resaltada (se dibuja tal como es el flujo).
      setSeleccion(null);
      await esperarRender();
      if (!svgRef.current) throw new Error('El diagrama no está visible.');
      const svgTexto = svgAutonomo(svgRef.current, encuadre, getComputedStyle(lienzoRef.current).backgroundColor);
      const base = `${nombreArchivo || 'iflow'}_diagrama_r${version}`;
      if (formato === 'svg') {
        descargarBlob(new Blob([svgTexto], { type: 'image/svg+xml;charset=utf-8' }), `${base}.svg`);
        return;
      }
      const png = await svgAPng(svgTexto, encuadre.w, encuadre.h);
      if (formato === 'png') {
        descargarBlob(png, `${base}.png`);
        return;
      }
      await onAdjuntar!(new File([png], `${base}.png`, { type: 'image/png' }));
      setAvisoExportar({ ok: true, texto: `Se adjuntó ${base}.png a la documentación.` });
    } catch (err: any) {
      setAvisoExportar({ ok: false, texto: err?.message || 'No se pudo exportar el diagrama.' });
    } finally {
      setExportando(false);
    }
  };

  // ── Edición en línea (solo el dueño) ──
  const editaEnLinea = !!onEditar && !!diagrama?.valoresVisibles;
  const nPendientes = Object.keys(pendientes).length;

  const confirmarEdicion = (clave: string, original: string) => {
    setPendientes((p) => {
      const { [clave]: _, ...resto } = p;
      return textoEdicion === original ? resto : { ...resto, [clave]: textoEdicion };
    });
    setEditando(null);
  };
  const deshacer = (clave: string) => setPendientes((p) => { const { [clave]: _, ...resto } = p; return resto; });

  const aplicarPendientes = async (cual: 'descargar' | 'guardar') => {
    setErrorPendientes(null);
    setOcupadoPendientes(cual);
    try {
      if (cual === 'descargar') {
        await descargarAdaptado(orgId, iflowId, version, pendientes);
      } else {
        const r = await guardarAdaptadoComoVersion(orgId, iflowId, version, pendientes, '');
        setPendientes({});
        await onGuardado?.(r.version, r.cambiados);
      }
    } catch (err: any) {
      setErrorPendientes(err?.message || 'No se pudieron aplicar los cambios.');
    } finally {
      setOcupadoPendientes(null);
    }
  };

  /** Un valor del panel: texto (o "••••" si está oculto), con lápiz para editarlo en línea. */
  const valorEnLinea = (clave: string | null, original: string, oculto: boolean, campo = '') => {
    const editable = !!clave && editaEnLinea;
    if (editable && editando === clave) {
      return (
        <div className="flex items-start gap-1.5">
          <textarea
            autoFocus
            rows={Math.min(6, Math.max(1, textoEdicion.split('\n').length, Math.ceil(textoEdicion.length / 70)))}
            value={textoEdicion}
            onChange={(e) => setTextoEdicion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); confirmarEdicion(clave!, original); }
              if (e.key === 'Escape') { e.stopPropagation(); setEditando(null); }
            }}
            maxLength={4000}
            spellCheck={false}
            className="flex-1 bg-gray-900 border border-cyan-500/60 rounded-md px-2 py-1 text-xs font-mono text-white focus:outline-none resize-y"
          />
          <button onClick={() => setDesdePerfil({ campo, clave: clave!, original })} className="p-1 text-gray-400 hover:text-cyan-300" title="Insertar desde un perfil de valores"><KeyRound className="w-4 h-4" /></button>
          <button onClick={() => confirmarEdicion(clave!, original)} className="p-1 text-emerald-300 hover:text-emerald-200" title="Aplicar (Enter)"><Check className="w-4 h-4" /></button>
          <button onClick={() => setEditando(null)} className="p-1 text-gray-400 hover:text-white" title="Cancelar (Esc)"><X className="w-4 h-4" /></button>
        </div>
      );
    }
    const pendiente = clave ? pendientes[clave] : undefined;
    const cambiado = pendiente !== undefined;
    const mostrado = cambiado ? pendiente : oculto ? '••••••••' : original;
    return (
      <div className="flex items-start gap-1.5">
        <p className={`flex-1 font-mono break-all ${cambiado ? 'text-cyan-300' : 'text-gray-200'}`}>
          {mostrado || <span className="text-gray-500 font-sans">(vacío)</span>}
          {cambiado && <span className="ml-1.5 text-[10px] text-cyan-400 font-sans">· modificado</span>}
        </p>
        {editable && (
          <button onClick={() => { setEditando(clave); setTextoEdicion(cambiado ? pendiente! : original); }}
            className="p-0.5 text-gray-400 hover:text-cyan-300" title="Editar este valor" aria-label="Editar este valor">
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
        {cambiado && (
          <button onClick={() => deshacer(clave!)} className="p-0.5 text-gray-400 hover:text-white" title="Deshacer este cambio" aria-label="Deshacer este cambio">
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  };

  const barraPendientes = nPendientes > 0 && (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 rounded-xl px-3 py-2 text-xs">
        <span className="text-cyan-200 flex-1 min-w-[12rem]">
          {nPendientes} cambio{nPendientes === 1 ? '' : 's'} pendiente{nPendientes === 1 ? '' : 's'} en la revisión {version}: se aplican juntos al .zip.
        </span>
        <button onClick={() => setGuardandoEnPerfil(true)} disabled={!!ocupadoPendientes}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-gray-300 hover:text-white disabled:opacity-40"
          title="Guarda estos cambios en un perfil de valores">
          <BookmarkPlus className="w-3.5 h-3.5" /> Guardar en un perfil
        </button>
        <button onClick={() => { setPendientes({}); setEditando(null); }} disabled={!!ocupadoPendientes}
          className="px-2.5 py-1 rounded-md text-gray-300 hover:text-white disabled:opacity-40">Descartar</button>
        <button onClick={() => aplicarPendientes('descargar')} disabled={!!ocupadoPendientes}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-white/10 text-gray-200 hover:border-cyan-500/40 hover:text-white disabled:opacity-40">
          {ocupadoPendientes === 'descargar' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Descargar .zip
        </button>
        {puedeGuardar && version === versionActual && (
          <button onClick={() => aplicarPendientes('guardar')} disabled={!!ocupadoPendientes}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white font-medium disabled:opacity-40">
            {ocupadoPendientes === 'guardar' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar como versión {versionActual + 1}
          </button>
        )}
      </div>
      {errorPendientes && <p className="text-xs text-red-400">{errorPendientes}</p>}
    </div>
  );

  // Qué se puede editar desde el panel: receptores y Content Modifier con constantes.
  const focoEditable = !seleccion ? ''
    : seleccion.tipo === 'arista' && seleccion.arista.adaptador && seleccion.arista.adaptador.direccion !== 'Sender' ? `adaptador:${seleccion.arista.id}:`
    : seleccion.tipo === 'nodo' && seleccion.nodo.subtipo === 'Enricher' && seleccion.nodo.detalle?.filas.some((f) => f.protegido) ? `cm:${seleccion.nodo.id}:`
    : '';

  const panel = seleccion && (
    <aside className={`bg-white/5 border border-white/10 rounded-xl p-4 space-y-3 text-sm ${ampliado ? 'w-80 flex-shrink-0 overflow-y-auto' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-white font-medium break-words">
            {seleccion.tipo === 'nodo'
              ? seleccion.nodo.nombre || NOMBRES_TIPO[seleccion.nodo.tipo]
              : seleccion.arista.nombre || (seleccion.arista.tipo === 'mensaje' ? 'Adaptador' : 'Flujo')}
          </p>
          <p className="text-xs text-gray-400">
            {seleccion.tipo === 'nodo'
              ? tipoLegible(seleccion.nodo)
              : seleccion.arista.tipo === 'mensaje' ? 'Conexión con un sistema (adaptador)' : 'Flujo de secuencia'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {focoEditable && onEditar && (
            <button onClick={() => { setAmpliado(false); onEditar(focoEditable); }}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-medium"
              title={version !== versionActual ? 'Se adapta la revisión actual' : undefined}>
              <PenLine className="w-3.5 h-3.5" /> Editar
            </button>
          )}
          <button onClick={() => setSeleccion(null)} className="text-gray-400 hover:text-white" aria-label="Cerrar detalle"><X className="w-4 h-4" /></button>
        </div>
      </div>

      {seleccion.tipo === 'arista' && seleccion.arista.adaptador && (() => {
        const ad = seleccion.arista.adaptador;
        const datos: [string, string][] = ([
          ['Tipo', ad.tipo],
          ['Dirección', ad.direccion === 'Sender' ? 'Emisor (entrada)' : ad.direccion === 'Receiver' ? 'Receptor (salida)' : ad.direccion],
          ['Protocolo', [ad.transporte, ad.mensaje && ad.mensaje !== 'None' ? ad.mensaje : ''].filter(Boolean).join(' · ')],
          ['Autenticación', ad.autenticacion],
          ['Conectividad', ad.conectividad ? etiquetaConectividad(ad.conectividad) : '']
        ] as [string, string][]).filter(([, v]) => v);
        return (
          <>
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
              {datos.map(([k, v]) => (
                <React.Fragment key={k}><dt className="text-gray-500">{k}</dt><dd className="text-gray-200 break-words">{v}</dd></React.Fragment>
              ))}
            </dl>
            {ad.valores.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-[11px] uppercase tracking-wider text-gray-500">Valores configurados</h4>
                  {botonValores}
                </div>
                <dl className="space-y-1.5 text-xs">
                  {ad.valores.map((v, i) => (
                    <div key={i}>
                      <dt className="text-gray-500">{NOMBRES_CAMPO[v.campo] || v.campo}{v.parametrizado && <span className="text-gray-600"> · parámetro</span>}</dt>
                      <dd>{valorEnLinea(v.clave, v.valor, ocultos && !v.parametrizado, v.campo)}</dd>
                    </div>
                  ))}
                </dl>
                {!diagrama?.valoresVisibles && <p className="text-[11px] text-gray-500">{LEYENDA_VALORES}</p>}
              </div>
            )}
          </>
        );
      })()}

      {seleccion.tipo === 'arista' && !seleccion.arista.adaptador && (seleccion.arista.condicion || seleccion.arista.porDefecto) && (
        <div className="space-y-1 text-xs">
          {seleccion.arista.porDefecto && <p className="text-amber-300">Ruta por defecto del router (se toma si ninguna otra condición se cumple).</p>}
          {seleccion.arista.condicion && (
            <>
              <h4 className="text-[11px] uppercase tracking-wider text-gray-500">Condición</h4>
              <p className="font-mono text-gray-200 break-all whitespace-pre-wrap bg-white/5 border border-white/10 rounded-md p-2">{seleccion.arista.condicion}</p>
            </>
          )}
        </div>
      )}

      {seleccion.tipo === 'nodo' && seleccion.nodo.detalle && (() => {
        const { campos, filas } = seleccion.nodo.detalle;
        const hayProtegidos = filas.some((f) => f.protegido);
        return (
          <>
            {campos.length > 0 && (
              <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
                {campos.map((c) => (
                  <React.Fragment key={c.etiqueta}><dt className="text-gray-500">{c.etiqueta}</dt><dd className="text-gray-200 font-mono break-all">{c.valor}</dd></React.Fragment>
                ))}
              </dl>
            )}
            {filas.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-[11px] uppercase tracking-wider text-gray-500">Headers, propiedades y body</h4>
                  {hayProtegidos && botonValores}
                </div>
                <ul className="space-y-1.5 text-xs">
                  {filas.map((f, i) => (
                    <li key={i} className="bg-white/5 border border-white/10 rounded-md px-2 py-1.5">
                      <p className="text-gray-400">
                        <span className="text-gray-500">{f.grupo}{f.accion ? ` · ${f.accion}` : ''}</span>
                        {f.nombre && <span className="text-white font-mono"> {f.nombre}</span>}
                        {f.tipo && <span className="text-gray-500"> · {f.tipo}</span>}
                      </p>
                      {(f.valor || f.clave) && valorEnLinea(f.clave, f.valor, f.protegido && ocultos)}
                    </li>
                  ))}
                </ul>
                {hayProtegidos && !diagrama?.valoresVisibles && <p className="text-[11px] text-gray-500">Valores constantes enmascarados. {LEYENDA_VALORES}</p>}
              </div>
            )}
          </>
        );
      })()}
    </aside>
  );

  const cabecera = (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      {!diagramaFijo && <label className="text-xs text-gray-400 inline-flex items-center gap-2">
        Revisión
        <select value={version} onChange={(e) => setVersion(Number(e.target.value))}
          className="bg-gray-900 border border-white/10 rounded-md px-2 py-1 text-xs text-white">
          {revisiones.map((n) => <option key={n} value={n}>{n}{n === versionActual ? ' (actual)' : ''}</option>)}
        </select>
      </label>}
      {diagrama && (() => {
        const nPasos = diagrama.nodos.filter((n) => n.tipo === 'paso').length;
        const nConexiones = diagrama.aristas.filter((a) => a.tipo === 'mensaje').length;
        return (
          <div className="flex items-center gap-3 ml-auto">
            {editaEnLinea && editablesDiagrama.length > 0 && (
              <button onClick={() => setAplicandoPerfil(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-cyan-500/40 text-xs text-cyan-200 hover:bg-cyan-500/10"
                title="Aplica las reglas de un perfil de valores a este iFlow (con vista previa)">
                <Wand2 className="w-3.5 h-3.5" /> Aplicar perfil
              </button>
            )}
            <span className="text-[11px] text-gray-500">
              {nPasos} paso{nPasos === 1 ? '' : 's'} · {nConexiones} {nConexiones === 1 ? 'conexión' : 'conexiones'}
            </span>
            <div className="relative">
              <button onClick={() => setMenuExportar((m) => !m)} disabled={exportando || cargando}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-white/10 text-xs text-gray-200 hover:border-cyan-500/40 hover:text-white disabled:opacity-40">
                {exportando ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Exportar <ChevronDown className="w-3 h-3" />
              </button>
              {menuExportar && (
                <div className="absolute right-0 mt-1 w-60 bg-gray-800 border border-white/10 rounded-lg shadow-xl z-10 py-1 text-xs">
                  <button onClick={() => exportar('png')} className="w-full flex items-center gap-2 px-3 py-2 text-gray-200 hover:bg-white/5"><ImageDown className="w-3.5 h-3.5" /> Descargar PNG</button>
                  <button onClick={() => exportar('svg')} className="w-full flex items-center gap-2 px-3 py-2 text-gray-200 hover:bg-white/5"><FileCode className="w-3.5 h-3.5" /> Descargar SVG</button>
                  {onAdjuntar && (
                    <button onClick={() => exportar('adjuntar')} className="w-full flex items-center gap-2 px-3 py-2 text-gray-200 hover:bg-white/5 border-t border-white/10"><Paperclip className="w-3.5 h-3.5" /> Adjuntar PNG a la documentación</button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}
      {avisoPerfil && <p className="w-full text-xs text-emerald-300">{avisoPerfil}</p>}
      {avisoExportar && (
        <p className={`w-full text-xs ${avisoExportar.ok ? 'text-emerald-300' : 'text-red-400'}`}>{avisoExportar.texto}</p>
      )}
    </div>
  );

  if (ampliado) {
    return (
      <div className="fixed inset-0 z-[60] bg-gray-900 p-4 flex flex-col gap-3">
        {cabecera}
        {barraPendientes}
        <div className="flex gap-3 flex-1 min-h-0">
          {lienzo}
          {panel}
        </div>
        {selectorPerfil}
        {modalesPerfil}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {cabecera}
      {lienzo}
      {barraPendientes}
      {panel}
      {selectorPerfil}
      {modalesPerfil}
    </div>
  );
}
